"use client"

import { useState, useEffect, useCallback } from "react"
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete,
} from "@mui/material"
import {
  Add as AddIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Save as SaveIcon,
} from "@mui/icons-material"
import { toast } from "react-toastify"
import { motion } from "framer-motion"
import axios from "axios"

const API_BASE_URL = process.env.REACT_APP_API_URL

const CreateOrderPage = () => {
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState([])
  const [slots, setSlots] = useState([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [formData, setFormData] = useState({
    userId: "",
    slotId: "",
    itemOption: "",
    reason: "",
    isEmergency: true,
  })

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem("idToken")
      const response = await axios.get(`${API_BASE_URL}/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { all: true }
      })
      const allUsers = response.data.data || []
      const activeUsers = allUsers.filter(user => user.status === 'active')
      setUsers(activeUsers)
    } catch (error) {
      console.error("Error fetching users:", error)
      toast.error("Failed to fetch users")
    }
  }, [])

  const fetchSlots = useCallback(async () => {
    try {
      const token = localStorage.getItem("idToken")
      const response = await axios.get(`${API_BASE_URL}/admin/slots`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const slotData = response.data.slot
      setSlots(slotData?.slots || [])
    } catch (error) {
      console.error("Error fetching slots:", error)
      toast.error("Failed to fetch slots")
    }
  }, [])

  useEffect(() => {
    fetchUsers()
    fetchSlots()
  }, [fetchUsers, fetchSlots])

  const handleSubmitEmergencyOrder = async () => {
    if (!formData.userId || !formData.slotId || !formData.itemOption || !formData.reason) {
      toast.error("Please fill all required fields")
      return
    }

    setLoading(true)
    try {
      const token = localStorage.getItem("idToken")
      const selectedSlot = slots.find(slot => slot.id === formData.slotId)
      const selectedUser = users.find(user => user.email === formData.userId)

      const orderData = {
        reason: `[ADMIN ORDER] ${formData.reason}`,
        period: selectedSlot?.name || "",
        mealType: formData.itemOption,
        isEmergencyOrder: true,
        targetUserId: formData.userId,
        targetUserName: selectedUser?.display_name || selectedUser?.name || "",
      }

      await axios.post(`${API_BASE_URL}/requests`, orderData, {
        headers: { Authorization: `Bearer ${token}` },
      })

      toast.success("Order created successfully!")
      setDialogOpen(false)
      setFormData({
        userId: "",
        slotId: "",
        itemOption: "",
        reason: "",
        isEmergency: true,
      })
    } catch (error) {
      console.error("Error creating order:", error)
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        toast.error("Session expired. Redirecting to login...")
        setTimeout(() => {
          localStorage.removeItem("idToken")
          window.location.href = "/login"
        }, 2000)
        return
      }
      toast.error(error.response?.data?.message || "Failed to create order")
    } finally {
      setLoading(false)
    }
  }

  const selectedSlot = slots.find(slot => slot.id === formData.slotId)

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box
          sx={{
            mb: 3,
            p: 3,
            background: "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
            borderRadius: 3,
            color: "white",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <AddIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight={800}>
                Create Order
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Create food orders for any user
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Create Order Button */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{
              background: "linear-gradient(135deg, #06d6a0 0%, #20d5d5 100%)",
              fontWeight: 700,
              px: 4,
              py: 1.5,
              borderRadius: 2,
            }}
          >
            Create Order
          </Button>
        </Paper>

        {/* Emergency Order Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 4 }
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, background: "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)", color: "white" }}>
            Create Order
          </DialogTitle>
          <DialogContent sx={{ py: 3 }}>
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Create Order
              </Typography>
              <Typography variant="body2">
                Create a food order for any user, bypassing normal time restrictions.
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Autocomplete
                  options={users}
                  getOptionLabel={(option) => `${option.display_name || option.name || 'Unknown'} (${option.email})`}
                  value={users.find(user => user.email === formData.userId) || null}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ ...prev, userId: newValue?.email || "" }))
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select User"
                      required
                      fullWidth
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <PersonIcon sx={{ mr: 1, color: "#64748b" }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {option.display_name || option.name || 'Unknown User'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Select Slot</InputLabel>
                  <Select
                    value={formData.slotId}
                    onChange={(e) => setFormData(prev => ({ ...prev, slotId: e.target.value, itemOption: "" }))}
                    label="Select Slot"
                  >
                    {slots.map((slot) => (
                      <MenuItem key={slot.id} value={slot.id}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <ScheduleIcon fontSize="small" />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {slot.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {slot.startTime} - {slot.endTime}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required disabled={!selectedSlot}>
                  <InputLabel>Item Option</InputLabel>
                  <Select
                    value={formData.itemOption}
                    onChange={(e) => setFormData(prev => ({ ...prev, itemOption: e.target.value }))}
                    label="Item Option"
                  >
                    {selectedSlot?.item_options?.map((option) => {
                      const isVeg = option.includes("(Veg)")
                      const isNonVeg = option.includes("(Non-Veg)")
                      
                      return (
                        <MenuItem key={option} value={option}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {isVeg && (
                              <Box sx={{ 
                                width: 8, 
                                height: 8, 
                                borderRadius: "50%", 
                                background: "#2e7d32" 
                              }} />
                            )}
                            {isNonVeg && (
                              <Box sx={{ 
                                width: 8, 
                                height: 8, 
                                borderRadius: "50%", 
                                background: "#d32f2f" 
                              }} />
                            )}
                            <Typography 
                              sx={{ 
                                color: isVeg ? "#2e7d32" : isNonVeg ? "#d32f2f" : "inherit",
                                fontWeight: (isVeg || isNonVeg) ? 600 : 400
                              }}
                            >
                              {option}
                            </Typography>
                          </Box>
                        </MenuItem>
                      )
                    }) || []}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Reason"
                  multiline
                  rows={3}
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Reason for creating this order..."
                  helperText="This reason will be logged"
                />
              </Grid>

              {selectedSlot && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, background: "rgba(32, 213, 213, 0.1)", borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                      Selected Slot Details:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      <Chip label={selectedSlot.name} color="primary" />
                      <Chip label={`${selectedSlot.startTime} - ${selectedSlot.endTime}`} variant="outlined" />
                      {selectedSlot.restaurants?.map((restaurant, idx) => (
                        <Chip key={idx} label={restaurant} size="small" />
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button
              onClick={() => setDialogOpen(false)}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitEmergencyOrder}
              variant="contained"
              disabled={loading}
              sx={{
                background: "linear-gradient(135deg, #06d6a0 0%, #20d5d5 100%)",
                borderRadius: 2,
                fontWeight: 700,
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon sx={{ mr: 1 }} />}
              {loading ? "Creating..." : "Create Order"}
            </Button>
          </DialogActions>
        </Dialog>
      </motion.div>
    </Container>
  )
}

export default CreateOrderPage